import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Skeleton } from '@rackd/ui';
import {
  IconClipboardCheck,
  IconUsers,
  IconBooks,
  IconFlag,
  IconArrowUpRight,
  IconCrown,
  IconUserCheck,
  IconCalendarStats,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { routes } from '@/config/routes';
import {
  useAdminStats,
  useSignupTrends,
  useTopPrograms,
  SignupTrendsChart,
  TopProgramsTable,
} from '@/features/analytics';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const }
  },
};

interface StatCardProps {
  title: string;
  value: string;
  description: string;
  icon: React.ReactNode;
  to?: string;
  trend?: { value: string; positive: boolean };
  loading?: boolean;
}

function StatCard({ title, value, description, icon, to, trend, loading }: StatCardProps) {
  const content = (
    <Card className={`p-5 ${to ? 'hover:border-[var(--accent-primary)]/30' : ''} transition-colors`}>
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 bg-[var(--accent-primary)]/10 rounded-xl flex items-center justify-center text-[var(--accent-primary)]">
          {icon}
        </div>
        {trend && !loading && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${
              trend.positive
                ? 'bg-[var(--success)]/10 text-[var(--success)]'
                : 'bg-[var(--error)]/10 text-[var(--error)]'
            }`}
          >
            {trend.value}
          </span>
        )}
      </div>
      <div className="mt-4">
        {loading ? (
          <>
            <Skeleton className="h-8 w-20 mb-2" />
            <Skeleton className="h-4 w-28 mb-1" />
            <Skeleton className="h-3 w-36" />
          </>
        ) : (
          <>
            <p className="text-3xl font-bold text-[var(--text)]">{value}</p>
            <p className="text-sm font-medium text-[var(--text-secondary)] mt-1">{title}</p>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">{description}</p>
          </>
        )}
      </div>
      {to && !loading && (
        <div className="mt-4 flex items-center gap-1 text-sm font-medium text-[var(--accent-primary)]">
          View details
          <IconArrowUpRight size={16} />
        </div>
      )}
    </Card>
  );

  if (to) {
    return <Link to={to}>{content}</Link>;
  }

  return content;
}

export function DashboardPage() {
  const { stats, isLoading: statsLoading } = useAdminStats();
  const { trends, isLoading: trendsLoading } = useSignupTrends(30);
  const [programSort, setProgramSort] = useState<'downloads' | 'rating' | 'recent'>('downloads');
  const { programs, isLoading: programsLoading } = useTopPrograms(programSort, 10);

  return (
    <motion.div
      className="space-y-8"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--text)]">Admin Dashboard</h1>
        <p className="text-sm text-[var(--text-tertiary)] mt-1">
          Overview of the Rackd platform
        </p>
      </div>

      {/* User Stats */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text)] mb-4">Users</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={stats?.totalUsers.toLocaleString() ?? '0'}
            description="All registered accounts"
            icon={<IconUsers size={24} />}
            to={routes.users}
            loading={statsLoading}
          />
          <StatCard
            title="Power Rack"
            value={stats?.powerRackUsers.toLocaleString() ?? '0'}
            description="Paid subscribers"
            icon={<IconCrown size={24} />}
            loading={statsLoading}
          />
          <StatCard
            title="Trial Users"
            value={stats?.trialUsers.toLocaleString() ?? '0'}
            description="Active trials"
            icon={<IconUserCheck size={24} />}
            loading={statsLoading}
          />
          <StatCard
            title="Free Users"
            value={stats?.freeUsers.toLocaleString() ?? '0'}
            description="Free tier accounts"
            icon={<IconUsers size={24} />}
            loading={statsLoading}
          />
        </div>
      </div>

      {/* Growth Stats */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text)] mb-4">Growth</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Signups Today"
            value={stats?.signupsToday.toString() ?? '0'}
            description="New accounts today"
            icon={<IconCalendarStats size={24} />}
            trend={
              stats && stats.signupsToday > 0
                ? { value: `+${stats.signupsToday}`, positive: true }
                : undefined
            }
            loading={statsLoading}
          />
          <StatCard
            title="This Week"
            value={stats?.signupsThisWeek.toString() ?? '0'}
            description="Signups this week"
            icon={<IconCalendarStats size={24} />}
            trend={
              stats && stats.signupsThisWeek > 0
                ? { value: `+${stats.signupsThisWeek}`, positive: true }
                : undefined
            }
            loading={statsLoading}
          />
          <StatCard
            title="This Month"
            value={stats?.signupsThisMonth.toString() ?? '0'}
            description="Signups this month"
            icon={<IconCalendarStats size={24} />}
            trend={
              stats && stats.signupsThisMonth > 0
                ? { value: `+${stats.signupsThisMonth}`, positive: true }
                : undefined
            }
            loading={statsLoading}
          />
        </div>
      </div>

      {/* Program Stats */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text)] mb-4">Programs</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Pending Reviews"
            value={stats?.pendingPrograms.toString() ?? '0'}
            description="Programs awaiting review"
            icon={<IconClipboardCheck size={24} />}
            to={routes.reviews}
            trend={
              stats && stats.pendingPrograms > 0
                ? { value: `${stats.pendingPrograms} pending`, positive: false }
                : undefined
            }
            loading={statsLoading}
          />
          <StatCard
            title="Approved"
            value={stats?.approvedPrograms.toString() ?? '0'}
            description="Published programs"
            icon={<IconBooks size={24} />}
            loading={statsLoading}
          />
          <StatCard
            title="Catalog Programs"
            value={stats?.totalPrograms.toString() ?? '0'}
            description="Official programs"
            icon={<IconBooks size={24} />}
            to={routes.catalog}
            loading={statsLoading}
          />
          <StatCard
            title="Flagged"
            value={stats?.flaggedPrograms.toString() ?? '0'}
            description="Requires moderation"
            icon={<IconFlag size={24} />}
            to={routes.moderation}
            trend={
              stats && stats.flaggedPrograms > 0
                ? { value: 'Action needed', positive: false }
                : undefined
            }
            loading={statsLoading}
          />
        </div>
      </div>

      {/* Signup Trends Chart */}
      <SignupTrendsChart data={trends} loading={trendsLoading} />

      {/* Top Programs Table */}
      <TopProgramsTable
        programs={programs}
        loading={programsLoading}
        currentSort={programSort}
        onSortChange={setProgramSort}
      />

      {/* Quick actions */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text)] mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to={routes.reviews}>
            <Card className="p-4 hover:border-[var(--accent-primary)]/30 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[var(--warning)]/10 rounded-lg flex items-center justify-center">
                  <IconClipboardCheck size={20} className="text-[var(--warning)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text)]">Review Programs</p>
                  <p className="text-xs text-[var(--text-tertiary)]">Process pending submissions</p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to={routes.catalogNew}>
            <Card className="p-4 hover:border-[var(--accent-primary)]/30 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[var(--accent-primary)]/10 rounded-lg flex items-center justify-center">
                  <IconBooks size={20} className="text-[var(--accent-primary)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text)]">Add Curated Program</p>
                  <p className="text-xs text-[var(--text-tertiary)]">Create official content</p>
                </div>
              </div>
            </Card>
          </Link>

          <Link to={routes.users}>
            <Card className="p-4 hover:border-[var(--accent-primary)]/30 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[var(--info)]/10 rounded-lg flex items-center justify-center">
                  <IconUsers size={20} className="text-[var(--info)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text)]">Manage Users</p>
                  <p className="text-xs text-[var(--text-tertiary)]">Search and manage accounts</p>
                </div>
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
